import { supabase } from '../../lib/supabase';

export interface ChangeLogParams {
  versionId: number;
  providerId: number;
  regionId: number;
  entityType: string;
  entityId: number;
  fieldName: string;
  actionType: 'CREATE' | 'UPDATE' | 'DELETE';
  oldValue?: string | null;
  newValue?: string | null;
}

export interface BulkChangeLogParams {
  versionId: number;
  providerId: number;
  regionId: number;
  entityType: string;
  entityId: number;
  changes: {
    fieldName: string;
    actionType: 'CREATE' | 'UPDATE' | 'DELETE';
    oldValue?: string | null;
    newValue?: string | null;
  }[];
}

export const changeTrackingService = {
  /**
   * Log a single field change
   */
  async logChange(params: ChangeLogParams) {
    try {
      const { error } = await (supabase as any)
        .from('change_log')
        .insert({
          version_id: params.versionId,
          provider_id: params.providerId,
          region_id: params.regionId,
          entity_type: params.entityType,
          entity_id: params.entityId,
          field_name: params.fieldName,
          action_type: params.actionType,
          old_value: params.oldValue?.toString() || null,
          new_value: params.newValue?.toString() || null,
        });

      if (error) {
        console.error('Failed to log change:', error);
        throw error;
      }
    } catch (error) {
      console.error('Change tracking error:', error);
      // Don't throw - we don't want change tracking to break the main operation
    }
  },

  /**
   * Log multiple field changes for a single entity at once
   */
  async logBulkChanges(params: BulkChangeLogParams) {
    try {
      const changeEntries = params.changes.map(change => ({
        version_id: params.versionId,
        provider_id: params.providerId,
        region_id: params.regionId,
        entity_type: params.entityType,
        entity_id: params.entityId,
        field_name: change.fieldName,
        action_type: change.actionType,
        old_value: change.oldValue?.toString() || null,
        new_value: change.newValue?.toString() || null,
      }));

      const { error } = await (supabase as any)
        .from('change_log')
        .insert(changeEntries);

      if (error) {
        console.error('Failed to log bulk changes:', error);
        throw error;
      }
    } catch (error) {
      console.error('Bulk change tracking error:', error);
      // Don't throw - we don't want change tracking to break the main operation
    }
  },

  /**
   * Helper to compare objects and generate change entries
   */
  generateFieldChanges(
    oldData: Record<string, any> | null,
    newData: Record<string, any> | null,
    fieldsToTrack: string[]
  ): Array<{
    fieldName: string;
    actionType: 'CREATE' | 'UPDATE' | 'DELETE';
    oldValue?: string | null;
    newValue?: string | null;
  }> {
    const changes: Array<{
      fieldName: string;
      actionType: 'CREATE' | 'UPDATE' | 'DELETE';
      oldValue?: string | null;
      newValue?: string | null;
    }> = [];

    if (!oldData && newData) {
      // CREATE - all fields are new
      for (const field of fieldsToTrack) {
        if (newData[field] !== undefined) {
          changes.push({
            fieldName: field,
            actionType: 'CREATE',
            oldValue: null,
            newValue: newData[field],
          });
        }
      }
    } else if (oldData && !newData) {
      // DELETE - all fields are removed
      for (const field of fieldsToTrack) {
        if (oldData[field] !== undefined) {
          changes.push({
            fieldName: field,
            actionType: 'DELETE',
            oldValue: oldData[field],
            newValue: null,
          });
        }
      }
    } else if (oldData && newData) {
      // UPDATE - compare fields
      for (const field of fieldsToTrack) {
        const oldValue = oldData[field];
        const newValue = newData[field];

        if (oldValue !== newValue) {
          changes.push({
            fieldName: field,
            actionType: 'UPDATE',
            oldValue,
            newValue,
          });
        }
      }
    }

    return changes;
  },

  /**
   * Helper to stringify complex values for storage
   */
  stringifyValue(value: any): string | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
    if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  },

  /**
   * Enhanced tracking for plan assignments with JSON format
   */
  async logPlanAssignment(params: {
    versionId: number;
    providerId: number;
    regionId: number;
    channelId: number;
    actionType: 'CREATE' | 'DELETE' | 'UPDATE';
    oldPlan?: { id: number; name: string; color: number } | null;
    newPlan?: { id: number; name: string; color: number } | null;
  }) {
    return this.logChange({
      versionId: params.versionId,
      providerId: params.providerId,
      regionId: params.regionId,
      entityType: 'channel_plans',
      entityId: params.channelId,
      fieldName: 'assignment',
      actionType: params.actionType,
      oldValue: params.oldPlan ? JSON.stringify(params.oldPlan) : null,
      newValue: params.newPlan ? JSON.stringify(params.newPlan) : null,
    });
  },

  /**
   * Enhanced tracking for category assignments with JSON format
   */
  async logCategoryAssignment(params: {
    versionId: number;
    providerId: number;
    regionId: number;
    channelId: number;
    actionType: 'CREATE' | 'DELETE' | 'UPDATE';
    oldCategory?: { id: number; name: string } | null;
    newCategory?: { id: number; name: string } | null;
  }) {
    return this.logChange({
      versionId: params.versionId,
      providerId: params.providerId,
      regionId: params.regionId,
      entityType: 'channel_categories',
      entityId: params.channelId,
      fieldName: 'assignment',
      actionType: params.actionType,
      oldValue: params.oldCategory ? JSON.stringify(params.oldCategory) : null,
      newValue: params.newCategory ? JSON.stringify(params.newCategory) : null,
    });
  },

  /**
   * Enhanced tracking for whole entity deletion
   */
  async logEntityDeletion(params: {
    versionId: number;
    providerId: number;
    regionId: number;
    entityType: string;
    entityId: number;
    entityData: { id: number; name: string; [key: string]: any };
  }) {
    return this.logChange({
      versionId: params.versionId,
      providerId: params.providerId,
      regionId: params.regionId,
      entityType: params.entityType,
      entityId: params.entityId,
      fieldName: 'entity',
      actionType: 'DELETE',
      oldValue: JSON.stringify(params.entityData),
      newValue: null,
    });
  },

  /**
   * Enhanced tracking for whole entity creation
   */
  async logEntityCreation(params: {
    versionId: number;
    providerId: number;
    regionId: number;
    entityType: string;
    entityId: number;
    entityData: { id: number; name: string; [key: string]: any };
  }) {
    return this.logChange({
      versionId: params.versionId,
      providerId: params.providerId,
      regionId: params.regionId,
      entityType: params.entityType,
      entityId: params.entityId,
      fieldName: 'entity',
      actionType: 'CREATE',
      oldValue: null,
      newValue: JSON.stringify(params.entityData),
    });
  },

  /**
   * Compare two versions and track whole channel additions/deletions
   */
  async trackVersionChannelChanges(params: {
    newVersionId: number;
    oldVersionId: number;
    providerId: number;
    regionId: number;
  }) {
    const { newVersionId, oldVersionId, providerId, regionId } = params;

    // Get channels from both versions
    const [newChannels, oldChannels] = await Promise.all([
      supabase
        .from('channels')
        .select('id, name, price, description, category_id, is_local, is_duplicate')
        .eq('version_id', newVersionId)
        .eq('provider_id', providerId),
      supabase
        .from('channels')
        .select('id, name, price, description, category_id, is_local, is_duplicate')
        .eq('version_id', oldVersionId)
        .eq('provider_id', providerId)
    ]);

    if (newChannels.error || oldChannels.error) {
      throw new Error('Failed to fetch channel data for comparison');
    }

    const newChannelIds = new Set(newChannels.data?.map(c => c.id) || []);
    const oldChannelIds = new Set(oldChannels.data?.map(c => c.id) || []);

    // Find deleted channels (exist in old but not in new)
    const deletedChannels = oldChannels.data?.filter(channel => !newChannelIds.has(channel.id)) || [];

    // Find created channels (exist in new but not in old)
    const createdChannels = newChannels.data?.filter(channel => !oldChannelIds.has(channel.id)) || [];

    // Track deleted channels
    for (const channel of deletedChannels) {
      await this.logEntityDeletion({
        versionId: newVersionId, // Log against the new version
        providerId,
        regionId,
        entityType: 'channels',
        entityId: channel.id,
        entityData: {
          id: channel.id,
          name: channel.name,
          price: channel.price,
          description: channel.description,
          category_id: channel.category_id,
          is_local: channel.is_local,
          is_duplicate: channel.is_duplicate
        }
      });
    }

    // Track created channels
    for (const channel of createdChannels) {
      await this.logEntityCreation({
        versionId: newVersionId,
        providerId,
        regionId,
        entityType: 'channels',
        entityId: channel.id,
        entityData: {
          id: channel.id,
          name: channel.name,
          price: channel.price,
          description: channel.description,
          category_id: channel.category_id,
          is_local: channel.is_local,
          is_duplicate: channel.is_duplicate
        }
      });
    }

    return {
      deletedChannels: deletedChannels.length,
      createdChannels: createdChannels.length,
      deletedChannelNames: deletedChannels.map(c => c.name),
      createdChannelNames: createdChannels.map(c => c.name)
    };
  },
};